// The operator's agenda on the device (D-151, D-152).
//
// Seven days, from today: what the server answered, kept whole so the days
// render without connectivity. The store has a freshness of its own, outside
// the day preparation, for two reasons: a preparation that fails must leave
// the agenda exactly as it was (criterion 53), and the agenda is worth
// refreshing on its own whenever the application opens with signal.
//
// Nothing of the quota or the compliance of a period is read or kept: the
// operator never sees either (D-180). With planning off there is no agenda,
// which is not staleness: the freshness record says so, and the screens show
// the day without slots instead of an error.
import type { ApiClient } from '../api/client';
import type { AgendaEntry } from '../api/types';
import type { AgendaRecord, FieldcoreDb } from '../db/database';
import { getSessionRecord } from '../db/database';

export const AGENDA_PATH = '/api/v1/agenda';

/** How long a stored agenda is used before it is read again with signal. */
export const AGENDA_FRESH_MS = 15 * 60 * 1000;

/** The days the agenda covers, today included. */
export const AGENDA_DAYS = 7;

/** A day as the organization reads it: the ISO date in its timezone. Since
 *  INC-14a-3f-1 it lives in the core of the product's format, the one source
 *  of the field that asks the browser for a date. */
export { localDate } from '../app/format';

/** The day `offset` days after `day`, as a date string. */
export function addDays(day: string, offset: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + offset);
  return date.toISOString().slice(0, 10);
}

/** The seven days the agenda covers, from today. */
export function agendaWindow(today: string): string[] {
  return Array.from({ length: AGENDA_DAYS }, (_, index) => addDays(today, index));
}

function recordOf(entry: AgendaEntry, operatorId: string, fetchedAt: number): AgendaRecord {
  return {
    id: entry.id,
    site_id: entry.site_id,
    period_start: entry.period_start,
    period_end: entry.period_end,
    suggested_date: entry.suggested_date,
    original_date: entry.original_date,
    source: entry.source,
    status: entry.status,
    role: entry.role,
    lead_operator_id: entry.lead_operator_id,
    crew_lead_operator_id: entry.crew.lead_operator_id,
    companion_operator_ids: entry.crew.companion_operator_ids,
    reschedule_reason: entry.reschedule_reason,
    cancel_reason: entry.cancel_reason,
    // D-225: the day the slot was done, when the server knows one. A server
    // that does not answer it yet leaves null, and no day is invented.
    consumed_date: entry.consumed_date ?? null,
    // B-40, tramo 2a (R-29): the hours, null for a visit with none and from a
    // server that does not answer them yet.
    start_time: entry.start_time ?? null,
    end_time: entry.end_time ?? null,
    // B-76 (D-489): the hours before it was moved, null for one with none and
    // from a server that does not answer them yet.
    original_start_time: entry.original_start_time ?? null,
    original_end_time: entry.original_end_time ?? null,
    operator_id: operatorId,
    fetched_at: fetchedAt,
  };
}

export interface AgendaRefresh {
  planningEnabled: boolean;
  entries: number;
  fetchedAt: number;
}

/** Read the agenda and replace the stored one, in one transaction: either the
 *  previous seven days stand whole or the new ones do, never a mixture.
 *
 *  With planning off nothing is read: the store is emptied and the freshness
 *  record says the organization has no agenda, so no screen asks again. */
export async function refreshAgenda(
  api: ApiClient,
  database: FieldcoreDb,
  input: { operatorId: string; planningEnabled: boolean; today: string },
): Promise<AgendaRefresh> {
  const fetchedAt = Date.now();
  const window = agendaWindow(input.today);
  const from = window[0] ?? input.today;
  const to = window[window.length - 1] ?? input.today;
  if (!input.planningEnabled) {
    await database.transaction('rw', [database.agenda, database.session], async () => {
      await database.agenda.clear();
      await database.session.put({
        key: 'agenda',
        fetchedAt,
        operatorId: input.operatorId,
        from,
        to,
        planningEnabled: false,
      });
    });
    return { planningEnabled: false, entries: 0, fetchedAt };
  }
  const entries = await api.get<AgendaEntry[]>(AGENDA_PATH);
  const records = entries
    .filter((entry) => entry.suggested_date >= from && entry.suggested_date <= to)
    .map((entry) => recordOf(entry, input.operatorId, fetchedAt));
  await database.transaction('rw', [database.agenda, database.session], async () => {
    await database.agenda.clear();
    await database.agenda.bulkPut(records);
    await database.session.put({
      key: 'agenda',
      fetchedAt,
      operatorId: input.operatorId,
      from,
      to,
      planningEnabled: true,
    });
  });
  return { planningEnabled: true, entries: records.length, fetchedAt };
}

/** Whether the stored agenda should be read again: another operator's, older
 *  than its freshness, or covering days that have gone by. Never with planning
 *  off, and never without signal — the caller decides that. */
export async function agendaIsStale(
  database: FieldcoreDb,
  input: { operatorId: string; today: string; now?: number },
): Promise<boolean> {
  const record = await getSessionRecord(database, 'agenda');
  if (record === undefined) return true;
  if (!record.planningEnabled) return false;
  if (record.operatorId !== input.operatorId) return true;
  if (record.from !== input.today) return true;
  return (input.now ?? Date.now()) - record.fetchedAt > AGENDA_FRESH_MS;
}

/** The operator's stored slots, in day order. Another operator's are not read. */
export async function storedAgenda(
  database: FieldcoreDb,
  operatorId: string,
): Promise<AgendaRecord[]> {
  const rows = await database.agenda.where('operator_id').equals(operatorId).toArray();
  return rows.sort(
    (one, other) =>
      one.suggested_date.localeCompare(other.suggested_date) || one.id.localeCompare(other.id),
  );
}

/** A slot is rescheduled because it carries the date it was moved from, not
 *  because of its status (D-188): reassigning it puts it back to assigned. */
export function isRescheduled(entry: AgendaRecord): boolean {
  return entry.original_date !== null;
}

/** B-76 (D-489): what the line of a slot moved says, by the office's rule for
 *  "Movida": moved to another day, the day it was moved from, whatever its
 *  hours did; on the same day with another hour of start, the hour it had;
 *  and nothing for one moved on its own day with no hour before it or with
 *  the same start. A record stored before the hours travelled has none. */
export type MovedFrom = { kind: 'day'; day: string } | { kind: 'hour'; start: string } | null;

export function movedFrom(entry: AgendaRecord): MovedFrom {
  if (entry.original_date === null) return null;
  if (entry.original_date !== entry.suggested_date)
    return { kind: 'day', day: entry.original_date };
  const before = entry.original_start_time ?? null;
  if (before !== null && before !== (entry.start_time ?? null)) {
    return { kind: 'hour', start: before };
  }
  return null;
}

/** A cancelled slot: shown struck through with its reason, and never opened. */
export function isCancelled(entry: AgendaRecord): boolean {
  return entry.status === 'cancelled';
}

/** A slot the first close of its visit already consumed: the day's work done. */
export function isDone(entry: AgendaRecord): boolean {
  return entry.status === 'consumed';
}

/** What the operator can still start: neither cancelled nor already done. */
export function isOpen(entry: AgendaRecord): boolean {
  return !isCancelled(entry) && !isDone(entry);
}

export interface AgendaDay {
  day: string;
  entries: AgendaRecord[];
}

/** The seven days from today, each with its slots, empty days included. */
export function byDay(entries: AgendaRecord[], today: string): AgendaDay[] {
  return agendaWindow(today).map((day) => ({
    day,
    entries: entries.filter((entry) => entry.suggested_date === day),
  }));
}

/** The first day after today that has a slot worth going to, or null. */
export function nextDayWithEntries(entries: AgendaRecord[], today: string): string | null {
  for (const day of agendaWindow(today).slice(1)) {
    if (entries.some((entry) => entry.suggested_date === day && isOpen(entry))) return day;
  }
  return null;
}
